import {
  FEATURES,
  GROUP_TYPE,
  IGroupInfo,
  ISelectedMembers,
} from "@home-health-notify/domain";
// Call button hidden for now — may be implemented later
// import CallIcon from "@mui/icons-material/Call";
import InfoIcon from "@mui/icons-material/Info";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import PersonPinCircleIcon from "@mui/icons-material/PersonPinCircle";
import SearchIcon from "@mui/icons-material/Search";
import {
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
} from "@mui/material";
import {
  AddMembersContext,
  ApiErrorHandler,
  OrganizationContext,
  useTranslation,
} from "hhn-commons";
import { useSnackbar } from "notistack";
import { useContext, useEffect, useRef, useState } from "react";
import { RightDrawerContext } from "../../../context/RightDrawerContext";
import { ActionIcons } from "../../MessageHeader.style";
// Call button hidden for now — may be implemented later
// import CallDrawer from "./CallDrawer";
import {
  getAdvanceSearchFilters,
  getPatientHeaderProps,
  isAddressEmpty,
} from "../../../addMember/addUsers/advanceSearch/map/AdvanceMapHelper";
import {
  RestAdvanceSearchDialogProps,
  useAdvanceSearchDialog,
} from "../../../context/AdvanceSearchDialogContext";

export const HeaderActionComponent = ({
  groupInfo,
  onMessageSearch,
  isMemberFlag,
  openChatInfo,
  isChatLoading,
}: {
  groupInfo: IGroupInfo;
  onMessageSearch: () => void;
  isMemberFlag: boolean;
  openChatInfo: () => void;
  isChatLoading: boolean;
}) => {
  const { t } = useTranslation();
  const tNs = (key: string) => t(key, { ns: "message" });
  const tNAdvanceSearch = (key: string) => t(key, { ns: "advanceSearch" });
  // openDrawer + setContent were only used by the (hidden) call drawer
  const { openAddMembersDrawer } = useContext(RightDrawerContext);
  const { selectedOrgUser } = useContext(OrganizationContext);
  const { enqueueSnackbar } = useSnackbar();

  const isAddMembersAllowed = selectedOrgUser?.hasAccess(FEATURES.ADD_MEMBERS);

  const openAddMembers = () => {
    if (isChatLoading) return;
    openAddMembersDrawer(groupInfo, false);
  };

  const { resetMembers } = useContext(AddMembersContext);
  const { openDialog } = useAdvanceSearchDialog();

  const closeAdvanceSearchModal = () => {
    selectedOrgUser?.chatManager
      .getPatientChatManager()
      .resetAdvanceSearchClinicianList();
    resetMembers();
  };

  const isPatientGroup = groupInfo?.groupType === GROUP_TYPE.PATIENT;

  const onAddMemberCallback = async (selectedMembers: ISelectedMembers) => {
    const chatId: string = groupInfo.id;
    try {
      if (isPatientGroup) {
        await selectedOrgUser?.chatManager
          .getPatientChatManager()
          .addMembers(selectedMembers, chatId);
      }
      enqueueSnackbar(groupInfo?.name + tNs("membersAddedSuccessfully"), {
        autoHideDuration: 3000,
      });
    } catch (e) {
      ApiErrorHandler(e, enqueueSnackbar);
    }
  };

  const openAddMembersFromMap = () => {
    if (isChatLoading) return;
    const advanceSearchFilters = getAdvanceSearchFilters(groupInfo);
    if (isAddressEmpty(advanceSearchFilters)) {
      enqueueSnackbar(tNAdvanceSearch("updatePatientAddressMessage"), {
        autoHideDuration: 3000,
      });
    } else {
      openDialog(
        {
          advanceSearchFilters,
          existingMembers: groupInfo.members,
          existingGroup: true,
          patientHeaderProps: getPatientHeaderProps(groupInfo),
          suggestedCliniciansDepartments: [],
          closeModal: closeAdvanceSearchModal,
          onAddMemberCallback,
        } as RestAdvanceSearchDialogProps,
        "chat_header",
      );
    }
  };

  // Call button hidden for now — may be implemented later
  // const openCallDrawer = () => {
  //   if (isChatLoading) return;
  //   setContent(
  //     <CallDrawer
  //       isLoading={false}
  //       chatId={groupInfo.id}
  //       groupType={groupInfo.groupType}
  //     />,
  //   );
  //   openDrawer();
  //   enqueueSnackbar(t("notImplemented"), {
  //     autoHideDuration: 3000,
  //   });
  // };

  const hasAccessAdvanceSearch = selectedOrgUser?.hasAccess(
    FEATURES.ADVANCE_SEARCH,
  );

  // Compact mode (3-dot overflow menu) kicks in when the chat header's
  // available width is tight (<600px)
  const COMPACT_THRESHOLD_PX = 600;
  const actionsRef = useRef<HTMLDivElement>(null);
  const [isCompact, setIsCompact] = useState(false);

  useEffect(() => {
    const parent = actionsRef.current?.parentElement;
    if (!parent) return;
    const observer = new ResizeObserver(entries => {
      for (const entry of entries) {
        setIsCompact(entry.contentRect.width < COMPACT_THRESHOLD_PX);
      }
    });
    observer.observe(parent);
    return () => observer.disconnect();
  }, []);

  const showInlineIcons = !isCompact;

  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const isMenuOpen = Boolean(menuAnchorEl);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const showOverflowMenu =
    !showInlineIcons &&
    ((isPatientGroup && isMemberFlag && hasAccessAdvanceSearch) ||
      (isMemberFlag && isAddMembersAllowed));
  // Note: dropped a standalone `|| isMemberFlag` term that only surfaced the
  // (now hidden) call option — kept it out so the overflow menu isn't empty.

  return (
    <ActionIcons data-testid="actionItems" ref={actionsRef}>
      {showInlineIcons && (
        <>
          {isPatientGroup && isMemberFlag && hasAccessAdvanceSearch && (
            <IconButton onClick={openAddMembersFromMap} disableTouchRipple>
              <Tooltip title={tNs("addMembersFromMap")}>
                <PersonPinCircleIcon data-testid="open-add-members-from-map" />
              </Tooltip>
            </IconButton>
          )}
          {isMemberFlag && isAddMembersAllowed && (
            <IconButton onClick={openAddMembers} disableTouchRipple>
              <Tooltip title={tNs("addMember")}>
                <PersonAddAlt1Icon data-testid="add-members" />
              </Tooltip>
            </IconButton>
          )}
        </>
      )}
      <IconButton onClick={onMessageSearch} disableTouchRipple>
        <Tooltip title={tNs("searchChat")}>
          <SearchIcon data-testid="SearchIcon" />
        </Tooltip>
      </IconButton>
      {/* Call button hidden for now — may be implemented later
      {showInlineIcons && isMemberFlag && (
        <IconButton
          onClick={openCallDrawer}
          disableTouchRipple
          data-testid="voice-call"
          disabled
        >
          <Tooltip title={tNs("voiceCall")}>
            <CallIcon />
          </Tooltip>
        </IconButton>
      )} */}
      <IconButton onClick={openChatInfo} disableTouchRipple>
        <Tooltip title={tNs("chatInfo")}>
          <InfoIcon data-testid="chatInfo-btn" />
        </Tooltip>
      </IconButton>
      {showOverflowMenu && (
        <>
          <IconButton
            onClick={handleMenuOpen}
            disableTouchRipple
            data-testid="more-options"
          >
            <Tooltip title={tNs("moreOptions")}>
              <MoreVertIcon />
            </Tooltip>
          </IconButton>
          <Menu
            anchorEl={menuAnchorEl}
            open={isMenuOpen}
            onClose={handleMenuClose}
          >
            {isPatientGroup && isMemberFlag && hasAccessAdvanceSearch && (
              <MenuItem
                onClick={() => {
                  handleMenuClose();
                  openAddMembersFromMap();
                }}
                data-testid="open-add-members-from-map"
              >
                <ListItemIcon>
                  <PersonPinCircleIcon />
                </ListItemIcon>
                <ListItemText>{tNs("addMembersFromMap")}</ListItemText>
              </MenuItem>
            )}
            {isMemberFlag && isAddMembersAllowed && (
              <MenuItem
                onClick={() => {
                  handleMenuClose();
                  openAddMembers();
                }}
                data-testid="add-members"
              >
                <ListItemIcon>
                  <PersonAddAlt1Icon />
                </ListItemIcon>
                <ListItemText>{tNs("addMember")}</ListItemText>
              </MenuItem>
            )}
            {/* Call button hidden for now — may be implemented later
            {isMemberFlag && (
              <MenuItem
                onClick={() => {
                  handleMenuClose();
                  openCallDrawer();
                }}
                data-testid="voice-call"
                disabled
              >
                <ListItemIcon>
                  <CallIcon />
                </ListItemIcon>
                <ListItemText>{tNs("voiceCall")}</ListItemText>
              </MenuItem>
            )} */}
          </Menu>
        </>
      )}
    </ActionIcons>
  );
};
